// Keep three upcoming pages ready, and share work with foreground navigation.
export function createPagePreloader({ loadPage, onError = () => {}, retryDelays = [1000, 3000] }) {
  const lookAhead = 3;
  const ready = new Map();
  const pending = new Map();
  const failed = new Set();
  let selected = 1;
  let total = 0;
  let stopped = false;

  function temporary(error) {
    const status = error.response?.status;
    return status === 408 || status === 429 || status >= 500 ||
      (!status && (error instanceof TypeError || ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error.code)));
  }

  function pump() {
    if (stopped || !ready.has(selected)) return;
    let background = [...pending.keys()].filter(page => page !== selected).length;
    for (const page of Array.from({ length: lookAhead }, (_, index) => selected + index + 1)) {
      if (background >= lookAhead) break;
      if (page > total || ready.has(page) || pending.has(page) || failed.has(page)) continue;
      background++;
      request(page).catch(() => {});
    }
  }

  function request(page) {
    if (ready.has(page)) return Promise.resolve(ready.get(page));
    if (pending.has(page)) return pending.get(page).promise;
    if (stopped) return Promise.reject(new Error('Reader closed'));
    failed.delete(page);
    let resolveJob;
    let rejectJob;
    const job = { promise: new Promise((resolve, reject) => { resolveJob = resolve; rejectJob = reject; }), timer: null, attempt: 0 };
    job.reject = rejectJob;
    pending.set(page, job);
    const run = async () => {
      try {
        const result = await loadPage(page);
        if (!result?.audioUrl || result.translationError) throw new Error('Page audio is not ready');
        ready.set(page, result);
        pending.delete(page);
        resolveJob(result);
        pump();
      } catch (error) {
        if (!stopped && temporary(error) && job.attempt < retryDelays.length) {
          const delay = retryDelays[job.attempt++];
          job.timer = setTimeout(run, delay);
          return;
        }
        pending.delete(page);
        failed.add(page);
        if (!stopped) onError(page, error);
        rejectJob(error);
        pump();
      }
    };
    // Foreground requests bypass background slots; no new background work starts
    // until the selected page is fully downloaded.
    run();
    return job.promise;
  }

  return {
    request,
    updateWindow(page, pageCount) { selected = page; total = pageCount; pump(); },
    stop() {
      stopped = true;
      for (const job of pending.values()) {
        // Keep completed downloads; only stop scheduled retries.
        if (job.timer) { clearTimeout(job.timer); job.reject(new Error('Reader closed')); }
      }
    },
  };
}
